import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { toast } from "sonner";

import { extractApiError } from "@/shared/lib/apiError";

import { apiClient } from "./apiClient";

/**
 * RTK Query → Axios bridge (PLX §6). Replaces `fetchBaseQuery` so the whole
 * data layer speaks through one axios client (interceptors for auth + error
 * normalisation).
 *
 * `responseHandler` receives the raw AxiosResponse (with `responseType:"blob"`
 * so `response.data` is a Blob) — used by PDF/Excel/card-download endpoints.
 * This mirrors the old fetch `responseHandler(response)` contract, except the
 * argument is an AxiosResponse and the payload is `response.data`.
 */
export interface AxiosBaseQueryArgs {
  url: string;
  method?: AxiosRequestConfig["method"];
  data?: AxiosRequestConfig["data"];
  body?: AxiosRequestConfig["data"];
  params?: AxiosRequestConfig["params"];
  headers?: AxiosRequestConfig["headers"];
  responseHandler?: (response: AxiosResponse) => unknown | Promise<unknown>;
}

export interface AxiosBaseQueryError {
  status: number;
  data: unknown;
}

/** Endpoints that own their failure UI — no global toast for these. */
const SILENT_ENDPOINTS = new Set([
  "login",
  "sendOtp",
  "verifyOtp",
  "getMe",
  "logout",
  "resetPassword",
  // The consolidated-bill pay page owns its own failure UI (conflict banners, the
  // amount-changed dialog, the terminal 502 card). Keep these off the global toast.
  "getStatementInfo",
  "quoteStatement",
  "chargeStatement",
  // Calculate explains its own refusals in the dialog (buckets with no split, a month that already has a
  // ledger, a month locked by approved rows) — a toast on top would repeat them in fewer words.
  "calculateDistribution",
  // The split writes answer their own refusals — a banner beside the field at fault, a per-row result, or
  // their own toast — so a global toast on top said the same failure twice, once in fewer words.
  "createPayoutSplit",
  "updatePayoutSplit",
  "deactivatePayoutSplit",
  "reorderPayoutSplits",
  "bulkApplySplits",
  // Place names are a nicety: a failed lookup shows "Div 12" instead, which is not worth a toast per employer.
  "getGroupsIdNameForScopes",
  "getDivisionsIdNameForScopes",
  "getLocationsIdNameForScopes",
]);

/** Statuses that are a normal answer rather than a failure worth interrupting for. */
const QUIET_STATUSES = new Set([401, 404]);

export const axiosBaseQuery =
  ({ client = apiClient }: { client?: AxiosInstance } = {}): BaseQueryFn<
    AxiosBaseQueryArgs,
    unknown,
    AxiosBaseQueryError,
    // No per-endpoint extra options — mirror RTK Query's default `{}` for this
    // generic slot (the empty-object type is intentional and confined here).
    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    {},
    { response: AxiosResponse }
  > =>
  async ({ url, method = "GET", data, body, params, headers, responseHandler }, api) => {
    try {
      const payload = body ?? data;
      const isForm = payload instanceof FormData;
      const requestHeaders: Record<string, unknown> = headers
        ? Object.fromEntries(Object.entries(headers))
        : {};
      if (isForm) delete requestHeaders["Content-Type"];

      const response = await client.request({
        url,
        method,
        data: payload,
        params,
        headers: requestHeaders as Record<string, string>,
        responseType: responseHandler ? "blob" : "json",
      });

      const finalData = responseHandler ? await responseHandler(response) : response.data;
      return { data: finalData, meta: { response } };
    } catch (axiosError) {
      const error = axiosError as AxiosError;
      const raw = error.response?.data;
      const failure = {
        status: error.response?.status ?? 500,
        // A download that fails still arrives as a Blob; read it, or the server's reason is lost.
        data: raw instanceof Blob ? await decodeBlobError(raw) : (raw ?? error.message),
      };
      reportFailure(failure, api.endpoint);
      return { error: failure };
    }
  };

/**
 * The single place a failed request becomes visible.
 *
 * Only a handful of the screens that run a query check `isError`, so a denied
 * or failed read rendered an empty table that read as "no data" — the same
 * ambiguity that makes a 403 look like a pass during testing. Reporting here
 * covers every endpoint without touching each component.
 *
 * 401 is deliberately quiet: the session is already being torn down by the
 * axios interceptor, and a toast on a page the user is being navigated away
 * from is noise.
 */
/** A failed blob request's body: the JSON error envelope when it is one, its text otherwise. */
async function decodeBlobError(blob: Blob): Promise<unknown> {
  let text = "";
  try {
    text = (await blob.text()).trim();
  } catch {
    return null;
  }
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { message: text };
  }
}

/** 409 codes a screen resolves with its own dialog — a toast on top would only repeat it. */
const HANDLED_CONFLICT_CODES = new Set(["INVOICE_OVERLAP"]);

function isHandledConflict(failure: AxiosBaseQueryError): boolean {
  if (failure.status !== 409) return false;
  const body = failure.data as { data?: { code?: unknown } } | null | undefined;
  const code = body && typeof body === "object" ? body.data?.code : undefined;
  return typeof code === "string" && HANDLED_CONFLICT_CODES.has(code);
}

function reportFailure(failure: AxiosBaseQueryError, endpoint: string): void {
  if (SILENT_ENDPOINTS.has(endpoint) || QUIET_STATUSES.has(failure.status)) return;
  if (isHandledConflict(failure)) return;

  if (failure.status === 403) {
    // The user is signed in, just not permitted for this scope — they stay
    // on the page.
    toast.error(extractApiError(failure, "You do not have access to this data."));
    return;
  }

  const message = extractApiError(failure, "Could not load data. Please try again.");
  // A server fault reads as its stack trace ("Could not open JPA EntityManager…", "Internal Server Error")
  // — meaningless to the person looking at the screen. Say what happened and what to do instead.
  toast.error(
    failure.status >= 500 && TECHNICAL_MESSAGE.test(message)
      ? "The server could not complete that just now. Please try again in a moment."
      : message
  );
}

/** Wording that comes from the server’s internals rather than from a message written for people. */
const TECHNICAL_MESSAGE =
  /internal server error|exception|jpa|hibernate|jdbc|sql|entitymanager|transaction|connection|timed out|null/i;
